/**
 * Keeps the signed-in agent "online" for the rider's header while the
 * Support Chat page is mounted: ant counts an agent online for 120 s after
 * their last chatAgentHeartbeat, so one goes out every 60 s.
 *
 * The tab may be hidden (agents work with the console in the background and
 * rely on its desktop notifications), so visibility does not stop the beat.
 * Idleness does: after 30 min with no key or mouse press in the page, and no
 * return to the tab, the agent is taken to have left and nothing is sent
 * until they are back, when a beat goes out at once.
 */

import { useEffect } from 'react';
import { sendAgentHeartbeat } from './api';

export const HEARTBEAT_INTERVAL_MS = 60000;
export const HEARTBEAT_IDLE_MS = 30 * 60000;

const ACTIVITY_EVENTS = ['keydown', 'mousedown'] as const;

export function useAgentHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    let lastSent: number | null = null;
    let lastActivity = Date.now();

    const idle = () => Date.now() - lastActivity >= HEARTBEAT_IDLE_MS;
    const beat = () => {
      if (idle()) return;
      lastSent = Date.now();
      // Presence is best effort: a failed beat is retried by the next one.
      sendAgentHeartbeat().catch(() => undefined);
    };
    const onActivity = () => {
      lastActivity = Date.now();
      // Back after a pause (idle, or a beat skipped): say so now, not in a minute.
      if (lastSent === null || Date.now() - lastSent >= HEARTBEAT_INTERVAL_MS) beat();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onActivity();
    };

    beat();
    const interval = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
    // Capture: a handler that stops propagation (Esc in the composer) still counts.
    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, true));
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach((type) =>
        window.removeEventListener(type, onActivity, true)
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
}
